import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
  Snackbar,
  TextField,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as JobIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as ProcessingIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import HealthIcon from '@mui/icons-material/HealthAndSafety';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { adminApi, AwsHealth, OverviewStats, ProviderHealthRecord, RecentJob, RecentRunGroup } from '../services/api';
import { toRunGroups, singletonGroup, patchJobInStats } from './dashboard-runs';
import { formatCredits, CREDITS_TOOLTIP } from '../utils/format';
import { OcrCostBreakdown, ocrCostTooltipTitle } from '../components/OcrCostBreakdown';
import { ADMIN_RUNS_PER_PAGE } from '../constants/pagination';
import {
  severityColor,
  severityFromAbsolute,
  severityFromPercent,
  severityIcon,
} from '../components/aws-health/severity';
import {
  getS3UploadsConsoleUrl,
  getS3OutputConsoleUrl,
  getS3OcrInputConsoleUrl,
  getS3AiResponseConsoleUrl,
  getCloudWatchLogsUrl,
} from '../config/aws-console';
import { IS_BROKER_ADMIN } from '../config/admin-build';

const COLORS = ['#38a169', '#e53e3e', '#dd6b20', '#3182ce'];

interface StatCardProps {
  title: string;
  value: number | string | null;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={color}>
            {value ?? '-'}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: `${color}15`,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Anthropic billing console — surfaced as the call-to-action when the
// provider tile is unhealthy. Hardcoded because there's only one billing
// page and it never changes.
const ANTHROPIC_BILLING_URL = 'https://console.anthropic.com/settings/billing';

// Google Cloud billing for the project the GCV service account belongs to.
// Every scanned-PDF and DOCX-image OCR path in the platform runs through that
// project, so when the GCV tile goes unhealthy this is the page you need —
// lapsed billing and exhausted credits both surface there.
//
// Project-scoped via env rather than hardcoded so the CTA follows the project
// if it is ever rotated, renamed, or differs per deployment — no code change.
//
// NOTE it does NOT hide the project id: Vite statically inlines
// import.meta.env.VITE_* at build time, so the value still lands in the
// compiled bundle exactly as a hardcoded string would. That is accepted — a
// GCP project id is not a credential, and this is an internal admin-only
// surface. Don't cite this indirection as a confidentiality measure.
//
// The fallback is the generic billing console: one extra click, but never a
// WRONG page, which is the right failure mode for a link followed mid-incident.
const GCV_BILLING_URL =
  import.meta.env.VITE_GCV_BILLING_URL || 'https://console.cloud.google.com/billing';

export function formatProcessingTimeShort(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

interface SlowestRunsTooltipDatum {
  jobId: string;
  displayName: string;
  userEmail: string;
  processingTimeMs: number;
}

interface SlowestRunsTooltipContentProps {
  active?: boolean;
  payload?: Array<{ payload: SlowestRunsTooltipDatum }>;
}

export function SlowestRunsTooltipContent({ active, payload }: SlowestRunsTooltipContentProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.25,
        boxShadow: 2,
        minWidth: 240,
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {row.displayName}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {row.userEmail}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ fontFamily: 'monospace', mt: 0.5, userSelect: 'all' }}
        data-testid="slowest-runs-tooltip-jobid"
      >
        {row.jobId}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: '#dd6b20', mt: 0.5, fontWeight: 500 }}
      >
        Processing time: {formatProcessingTimeShort(row.processingTimeMs)}
      </Typography>
    </Box>
  );
}

const PROVIDER_HEALTH_PRESENTATION: Record<
  'healthy' | 'degraded' | 'unhealthy' | 'unknown',
  { label: string; color: string }
> = {
  healthy: { label: 'Healthy', color: '#38a169' },
  degraded: { label: 'Degraded', color: '#dd6b20' },
  unhealthy: { label: 'Unhealthy', color: '#e53e3e' },
  unknown: { label: 'Unknown', color: '#718096' },
};

// Defensive: collapse whitespace + cap length on upstream-supplied error
// strings before rendering. React escapes text and attribute values, so
// XSS isn't the concern — but a hostile or pathological upstream message
// (multi-line, ANSI codes, multi-KB) would still ruin tile layout and
// the `title` tooltip.
function sanitizeUpstreamMessage(value: string): string {
  return value.replace(/[\r\n\t]+/g, ' ').slice(0, 500);
}

function formatLastChecked(lastCheckedAt: string | undefined): string {
  if (!lastCheckedAt) return 'never';
  const ageMs = Date.now() - new Date(lastCheckedAt).getTime();
  if (Number.isNaN(ageMs) || ageMs < 0) return 'just now';
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 min ago';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return '1 hr ago';
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

interface ProviderHealthCardProps {
  health: ProviderHealthRecord | null | undefined;
  /**
   * Which upstream this tile represents. Every prop below defaults to the
   * Anthropic values this card was originally written for, so the existing
   * call site stays byte-unchanged and its test IDs keep working — only the
   * GCV instance passes them (per general/abstract-before-duplicating.md:
   * extend with an option, don't fork a second near-identical card).
   */
  title?: string;
  billingUrl?: string;
  billingLinkLabel?: string;
  /** Prefix for the tile's data-testids, so two instances stay distinguishable. */
  testIdPrefix?: string;
}

const ProviderHealthCard: React.FC<ProviderHealthCardProps> = ({
  health,
  title = 'AI Provider (Anthropic)',
  billingUrl = ANTHROPIC_BILLING_URL,
  billingLinkLabel = 'Open Anthropic billing',
  testIdPrefix = 'provider-health',
}) => {
  const statusKey = health?.status ?? 'unknown';
  const { label, color } = PROVIDER_HEALTH_PRESENTATION[statusKey];
  const showAction = statusKey === 'unhealthy' || statusKey === 'degraded';

  return (
    <Card sx={{ height: '100%' }} data-testid={`${testIdPrefix}-tile`}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color }} data-testid={`${testIdPrefix}-status`}>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              Last check: {formatLastChecked(health?.lastCheckedAt)}
              {health?.latencyMs != null && health.status === 'healthy'
                ? ` · ${health.latencyMs} ms`
                : ''}
            </Typography>
            {health?.errorMessage && (() => {
              const safeMessage = sanitizeUpstreamMessage(health.errorMessage);
              return (
                <Typography
                  variant="caption"
                  title={safeMessage}
                  sx={{
                    color,
                    display: 'block',
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    fontWeight: 500,
                  }}
                  data-testid={`${testIdPrefix}-error`}
                >
                  {safeMessage}
                </Typography>
              );
            })()}
            {showAction && (
              <a
                href={billingUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '8px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color,
                  textDecoration: 'none',
                }}
                data-testid={`${testIdPrefix}-billing-link`}
              >
                {billingLinkLabel}
                <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
              </a>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              color,
              flexShrink: 0,
            }}
          >
            <HealthIcon />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Platform Saturation Row ───────────────────────────────────────────
//
// "If we hit 1000 we are going to have problems." The two tiles below
// surface the platform-saturation signals that translate directly into
// user-visible breakage: Lambda concurrency vs region quota, and DLQ
// depth (jobs that hit max-retries and are now stuck). Sourced from the
// existing /admin/aws-health endpoint so the math matches what the AWS
// Resources page shows. Calculation reminder for the reviewer/operator:
//   • concurrencyMaxInWindow = MAX of CloudWatch ConcurrentExecutions
//     (Maximum stat) over 1-day buckets across the rolling 30 days. The
//     "peak" is the worst day in the window — older spikes roll out
//     when 30 days elapse, which is the expected behaviour, not a bug.
//   • The peak timestamp below is derived from concurrencySpark so you
//     can sanity-check it against the AWS console.
const PlatformSaturationRow: React.FC<{
  health: AwsHealth | null;
  error: string | null;
}> = ({ health, error }) => {
  const concMax = health?.lambda.concurrencyMaxInWindow ?? null;
  const concLimit = health?.lambda.concurrencyLimit ?? null;
  const concPct = concMax !== null && concLimit ? (concMax / concLimit) * 100 : null;
  const concSeverity = concPct === null ? 'healthy' : severityFromPercent(concPct);

  // Find the timestamp of the peak bucket so the operator can verify
  // against the AWS console. concurrencySpark is asc; tie-breaks favour
  // the most-recent peak.
  const concPeakAt: string | null = (() => {
    if (!health || concMax === null) return null;
    let peakTs: string | null = null;
    let peakValue = -1;
    for (const point of health.lambda.concurrencySpark) {
      if (point.value >= peakValue) {
        peakValue = point.value;
        peakTs = point.ts;
      }
    }
    return peakTs;
  })();

  const dlqDepth = health?.sqs.dlqDepthTotal ?? 0;
  const dlqWithMessages = health?.sqs.dlqWithMessages ?? [];
  const dlqSeverity = dlqDepth ? severityFromAbsolute(dlqDepth, 1, 50) : 'healthy';

  // Lambda memory utilization peak — capacity-planning signal.
  // The backend pulls this from REPORT log lines via CloudWatch Logs Insights
  // (Lambda doesn't expose memory utilization as a default CW metric, and
  // we run a container Lambda so the Insights extension would require a
  // Dockerfile change). Sustained >80% means it's time to bump the
  // lambda_memory_size variable in tfvars (e.g. 2048 -> 3008).
  const memPct = health?.lambda.memoryUtilizationMaxPct ?? null;
  const memSeverity = memPct === null ? 'healthy' : severityFromPercent(memPct);

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card data-testid="dash-panel-lambda-concurrency">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Lambda concurrency (30d)
              </Typography>
              {severityIcon(concSeverity)}
            </Stack>
            {error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              {concMax ?? '—'}
              {concLimit && (
                <Typography component="span" variant="h5" color="text.secondary" sx={{ ml: 1 }}>
                  / {concLimit}
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              Peak in last 30d vs region quota
              {concPeakAt && (
                <>
                  {' · '}
                  <strong>peak hit {new Date(concPeakAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</strong>
                </>
              )}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Card data-testid="dash-panel-dlq-depth">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                DLQ messages (total)
              </Typography>
              {severityIcon(dlqSeverity)}
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 700, color: severityColor(dlqSeverity) }}>
              {dlqDepth}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dlqSeverity === 'healthy'
                ? 'Healthy — no failed jobs sitting in dead-letter queues'
                : `${dlqWithMessages.length} DLQ${dlqWithMessages.length === 1 ? '' : 's'} have undelivered messages`}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Card data-testid="dash-panel-lambda-memory">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Lambda memory peak (30d)
              </Typography>
              {severityIcon(memSeverity)}
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 700, color: severityColor(memSeverity) }}>
              {memPct !== null ? `${memPct.toFixed(0)}%` : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {memPct === null
                ? 'No REPORT log data yet — populates after Lambda invocations land in CloudWatch'
                : memSeverity === 'healthy'
                  ? 'Of allocated memory_size — room to spare, no bump needed'
                  : memSeverity === 'warning'
                    ? 'Approaching cap — watch for sustained pressure, consider bumping memory_size'
                    : 'Cap pressure — bump lambda_memory_size in tfvars (next step: 3008MB)'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [awsHealth, setAwsHealth] = useState<AwsHealth | null>(null);
  const [awsHealthError, setAwsHealthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const hasLoadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(ADMIN_RUNS_PER_PAGE);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [searchJobId, setSearchJobId] = useState<string>('');
  const [searchResult, setSearchResult] = useState<RecentJob | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | 'PROCESSING' | 'QUEUED' | 'COMPLETED' | 'FAILED'>('');
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  // jobIds currently mid-send. Disables the button + shows a spinner so the
  // admin can't double-click; the server's idempotency-guard is the real
  // backstop, this is the UX layer.
  const [notifyInFlight, setNotifyInFlight] = useState<Set<string>>(new Set());
  const [resolveInFlight, setResolveInFlight] = useState<Set<string>>(new Set());

  // Helper function to format milliseconds to human-readable duration
  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      const remainingSeconds = seconds % 60;
      return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!hasLoadedRef.current) {
          setLoading(true);
        } else {
          setTableLoading(true);
        }
        const response = await adminApi.getOverviewStats({
          recentJobsOffset: page * rowsPerPage,
          recentJobsLimit: rowsPerPage,
          recentJobsStatus: statusFilter || undefined
        });
        if (response.success) {
          setStats(response.data);
        } else {
          setError('Failed to load statistics');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load statistics');
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
        setTableLoading(false);
      }
    };

    fetchStats();
  }, [page, rowsPerPage, statusFilter]);

  // AWS health is fetched once on mount (and refreshed every 60s — the
  // backend caches at 60s anyway, so polling more often is wasted work).
  // Failure is non-fatal; the saturation row degrades to "—" so the rest
  // of the dashboard still renders.
  //
  // NOT fetched at all on the broker-admin build. `/admin/aws-health` is
  // `internalOnly` server-side (middleware/admin-authorizer.ts), so a broker
  // admin's call is a guaranteed 403 — the panel can never populate, it can
  // only sit in its degraded "—" state. The broker gate previously existed
  // only at the nav/route layer (HIDDEN_FOR_BROKER + the App.tsx redirect),
  // which hides the AWS Health *page*; this widget called the same endpoint
  // independently and so slipped straight past it. Reuse the same build gate
  // rather than re-deriving one. Trello #461: 1 181 rejected calls in 24h,
  // two broker admins, one per open tab per minute, forever.
  useEffect(() => {
    if (IS_BROKER_ADMIN) return;

    let cancelled = false;
    let interval: number | undefined;
    const stopPolling = () => {
      if (interval !== undefined) {
        window.clearInterval(interval);
        interval = undefined;
      }
    };

    const fetchAwsHealth = async () => {
      try {
        const result = await adminApi.getAwsHealth('30d');
        if (cancelled) return;
        if (result.success) {
          setAwsHealth(result.data);
          setAwsHealthError(null);
        }
      } catch (err: any) {
        if (cancelled) return;
        setAwsHealthError(err?.response?.data?.message || err?.message || 'Failed to load AWS health');
        // An auth verdict is not a transient failure — re-polling it every 60s
        // only manufactures log noise (and can never succeed without a reload,
        // by which point the token/build has changed anyway). Stop the timer.
        const status = err?.response?.status;
        if (status === 401 || status === 403) stopPolling();
      }
    };

    fetchAwsHealth();
    // Always armed: `fetchAwsHealth` is async, so its catch cannot run before
    // this line. A 401/403 from the first call therefore clears the interval a
    // microtask later rather than skipping it — same outcome, one less branch.
    interval = window.setInterval(fetchAwsHealth, 60_000);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, []);

  useEffect(() => {
    const trimmed = searchJobId.trim();
    if (!trimmed) {
      setSearchResult(null);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }
    if (trimmed.length < 8) {
      // Too short to be a meaningful job ID — don't hammer the backend
      setSearchResult(null);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await adminApi.getJobDetails(trimmed);
        if (cancelled) return;
        if (response.success) {
          setSearchResult(response.data);
          setSearchError(null);
        } else {
          setSearchResult(null);
          setSearchError('Job not found');
        }
      } catch (err: any) {
        if (cancelled) return;
        setSearchResult(null);
        if (err?.response?.status === 404) {
          setSearchError('Job not found');
        } else {
          setSearchError(err?.response?.data?.message || err?.message || 'Failed to look up job');
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [searchJobId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const statusData = stats?.jobsByStatus ? [
    { name: 'Completed', value: stats.jobsByStatus.COMPLETED || 0 },
    { name: 'Failed', value: stats.jobsByStatus.FAILED || 0 },
    { name: 'Processing', value: stats.jobsByStatus.PROCESSING || 0 },
    { name: 'Queued', value: stats.jobsByStatus.QUEUED || 0 },
  ].filter(d => d.value > 0) : [];

  // Build labels combining display name + run date so two slow runs of
  // the same job type on different days are distinguishable on the chart.
  const slowestRunsData = (stats?.slowestRunsLast30Days || []).map(run => ({
    ...run,
    label: `${run.displayName} · ${new Date(run.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
  }));

  const toggleRowExpansion = (jobId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage(label);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  /**
   * Fire the one-click "we noticed your job failed" email to the customer.
   * Optimistically marks the row notified locally; server is the source of
   * truth (next refresh will carry the persisted `failureNotifiedAt`) and
   * enforces idempotency (409 on double-send).
   */
  const handleNotifyCustomer = async (job: RecentJob) => {
    if (notifyInFlight.has(job.jobId) || job.failureNotifiedAt) return;
    setNotifyInFlight(prev => {
      const next = new Set(prev);
      next.add(job.jobId);
      return next;
    });
    try {
      const response = await adminApi.notifyJobFailure(job.jobId);
      const notifiedAt = response.data.notifiedAt;
      // Optimistically reflect on the flat table, the grouped rows, and search.
      setStats(prev => patchJobInStats(prev, job.jobId, { failureNotifiedAt: notifiedAt }));
      setSearchResult(prev =>
        prev && prev.jobId === job.jobId ? { ...prev, failureNotifiedAt: notifiedAt } : prev
      );
      setSnackbarMessage(`Notification sent to ${job.userEmail || 'customer'}`);
      setSnackbarOpen(true);
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.message || err?.message || 'Unknown error';
      if (status === 409) {
        // Lost the race against another admin — surface the persisted state.
        const existingNotifiedAt = err?.response?.data?.notifiedAt
          || new Date().toISOString();
        setStats(prev => patchJobInStats(prev, job.jobId, { failureNotifiedAt: existingNotifiedAt }));
        setSnackbarMessage('Customer was already notified');
      } else {
        setSnackbarMessage(`Failed to notify customer: ${detail}`);
      }
      setSnackbarOpen(true);
    } finally {
      setNotifyInFlight(prev => {
        const next = new Set(prev);
        next.delete(job.jobId);
        return next;
      });
    }
  };

  /**
   * Fire the one-click "good news, it's resolved" follow-up email. Only
   * reachable after the row was notified (the button is gated on
   * `failureNotifiedAt`). Optimistically marks the row resolved; server is the
   * source of truth and enforces idempotency (409 on double-send).
   */
  const handleMarkResolved = async (job: RecentJob) => {
    if (resolveInFlight.has(job.jobId) || job.failureResolvedAt || !job.failureNotifiedAt) return;
    setResolveInFlight(prev => {
      const next = new Set(prev);
      next.add(job.jobId);
      return next;
    });
    try {
      const response = await adminApi.notifyJobResolved(job.jobId);
      const resolvedAt = response.data.resolvedAt;
      // Optimistically reflect on the flat table, the grouped rows, and search.
      setStats(prev => patchJobInStats(prev, job.jobId, { failureResolvedAt: resolvedAt }));
      setSearchResult(prev =>
        prev && prev.jobId === job.jobId ? { ...prev, failureResolvedAt: resolvedAt } : prev
      );
      setSnackbarMessage(`Resolution email sent to ${job.userEmail || 'customer'}`);
      setSnackbarOpen(true);
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.message || err?.message || 'Unknown error';
      if (status === 409) {
        // Lost the race against another admin — surface the persisted state.
        const existingResolvedAt = err?.response?.data?.resolvedAt
          || new Date().toISOString();
        setStats(prev => patchJobInStats(prev, job.jobId, { failureResolvedAt: existingResolvedAt }));
        setSnackbarMessage('Customer was already told it was resolved');
      } else {
        setSnackbarMessage(`Failed to send resolution: ${detail}`);
      }
      setSnackbarOpen(true);
    } finally {
      setResolveInFlight(prev => {
        const next = new Set(prev);
        next.delete(job.jobId);
        return next;
      });
    }
  };

  // ── Recent Activity row rendering ─────────────────────────────────────────
  // A single row-renderer serves three roles: a standalone run, a Broker Doc
  // Assist chat's aggregate PARENT, and each chat TURN nested under that parent.
  // `group.groupId === group.parent.jobId` for every group (the backend sets a
  // conversation parent's jobId to the conversationId), so expand-state + the
  // `expand-row-<id>` testid key uniformly on `job.jobId`.

  /** The 11 data columns (User … Credits). `turnCount` renders the "N messages" chat badge. */
  const renderRunCells = (job: RecentJob, turnCount?: number) => (
    <>
      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {job.userEmail || job.userId.substring(0, 8)}
      </TableCell>
      <TableCell>
        {job.jobTypeDisplayName}
        {typeof turnCount === 'number' && (
          <Chip
            label={`${turnCount} ${turnCount === 1 ? 'message' : 'messages'}`}
            size="small"
            variant="outlined"
            sx={{ ml: 1, fontWeight: 500, fontSize: '0.7rem' }}
          />
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={job.status}
          size="small"
          color={
            job.status === 'COMPLETED' ? 'success' :
            job.status === 'FAILED' ? 'error' :
            job.status === 'PROCESSING' ? 'warning' : 'default'
          }
          variant="outlined"
          sx={{ fontWeight: 500, fontSize: '0.7rem' }}
        />
      </TableCell>
      <TableCell sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
        {typeof job.rating === 'number' ? (
          <Tooltip title={job.ratedAt ? `Rated on ${new Date(job.ratedAt).toLocaleString()}` : 'Rated by user'} arrow>
            <span style={{ color: job.rating <= 2 ? '#dc2626' : '#374151', fontWeight: 600 }}>
              {'★'.repeat(job.rating)}
              <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                {'☆'.repeat(5 - job.rating)}
              </span>
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        )}
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem' }}>
        {job.processingTimeMs ? formatDuration(job.processingTimeMs) : '-'}
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem', textAlign: 'right', fontFamily: 'monospace' }}>
        {job.inputTokens ? job.inputTokens.toLocaleString() : '-'}
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem', textAlign: 'right', fontFamily: 'monospace' }}>
        {job.outputTokens ? job.outputTokens.toLocaleString() : '-'}
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem', textAlign: 'right', fontFamily: 'monospace' }}>
        {job.thinkingChars
          ? Math.ceil(job.thinkingChars / 3.5).toLocaleString()
          : '-'}
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem' }}>
        {new Date(job.createdAt).toLocaleString()}
      </TableCell>
      <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
        {/* Trello #317 — Cost is the true all-in total (LLM + Google Vision OCR).
            When OCR contributed, hover shows the split. */}
        {job.ocrCostUsd ? (
          <Tooltip arrow title={ocrCostTooltipTitle(job.llmCostUsd ?? 0, job.ocrCostUsd, job.ocrPages ?? 0)}>
            <span>${job.costUsd.toFixed(4)}</span>
          </Tooltip>
        ) : (
          job.costUsd > 0 ? `$${job.costUsd.toFixed(4)}` : '$0.0000'
        )}
      </TableCell>
      <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
        {formatCredits(job.creditsCharged)}
      </TableCell>
    </>
  );

  /** The expanded detail panel for one run: S3 / CloudWatch links, failure actions, rating. */
  const renderRunDetail = (job: RecentJob) => (
    <Box sx={{ margin: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Job ID
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {job.jobId}
          </Typography>
          <Tooltip title="Copy full Job ID" arrow>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(job.jobId, `Job ID copied: ${job.jobId.substring(0, 12)}...`)}
              sx={{ padding: '4px' }}
            >
              <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            CloudWatch Logs
          </Typography>
          <Tooltip title="Copy CloudWatch Logs Insights URL for this job" arrow>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(
                getCloudWatchLogsUrl(job.jobId, job.createdAt),
                'CloudWatch Logs URL copied'
              )}
              sx={{ padding: '4px' }}
            >
              <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            S3 Uploads
          </Typography>
          <Tooltip title="Copy S3 console URL for this user's uploads" arrow>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(
                getS3UploadsConsoleUrl(job.userId),
                'S3 uploads path copied'
              )}
              sx={{ padding: '4px' }}
            >
              <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            S3 Output
          </Typography>
          {job.outputLocation ? (
            <Tooltip title={`Copy S3 output path: ${job.outputLocation}`} arrow>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(
                  getS3OutputConsoleUrl(job.outputLocation!),
                  'S3 output path copied'
                )}
                sx={{ padding: '4px' }}
              >
                <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.disabled">-</Typography>
          )}
        </Box>
        {job.outputLocation && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                OCR Input
              </Typography>
              <Tooltip title="Copy S3 URL for raw OCR input text" arrow>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(
                    getS3OcrInputConsoleUrl(job.outputLocation!),
                    'OCR input URL copied'
                  )}
                  sx={{ padding: '4px' }}
                >
                  <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                AI Response
              </Typography>
              <Tooltip title="Copy S3 URL for raw AI response text" arrow>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(
                    getS3AiResponseConsoleUrl(job.outputLocation!),
                    'AI response URL copied'
                  )}
                  sx={{ padding: '4px' }}
                >
                  <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
        {/* Trello #317 — Google Vision OCR cost breakdown (renders only when the run had paid OCR). */}
        <OcrCostBreakdown ocrCostUsd={job.ocrCostUsd} ocrPages={job.ocrPages} />
      </Box>
      {job.status === 'FAILED' && job.errorMessage && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
          <Typography variant="subtitle2" fontWeight={600} color="error.dark" gutterBottom>
            Error Details
          </Typography>
          <Typography variant="body2" color="error.dark" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {job.errorMessage}
          </Typography>
          {job.userEmail && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {job.failureNotifiedAt ? (
                <Tooltip title={`Sent at ${new Date(job.failureNotifiedAt).toLocaleString()}`} arrow>
                  <span>
                    <Button
                      data-testid={`notify-customer-${job.jobId}`}
                      size="small"
                      variant="contained"
                      color="error"
                      startIcon={<MailOutlineIcon />}
                      disabled
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        bgcolor: 'rgba(255,255,255,0.85)',
                        color: 'error.dark',
                        '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.85)', color: 'error.dark' },
                      }}
                    >
                      Notified {new Date(job.failureNotifiedAt).toLocaleDateString()}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={`Email ${job.userEmail} — "we're looking into your failed run"`} arrow>
                  <span>
                    <Button
                      data-testid={`notify-customer-${job.jobId}`}
                      size="small"
                      variant="contained"
                      color="error"
                      startIcon={
                        notifyInFlight.has(job.jobId)
                          ? <CircularProgress size={14} thickness={5} />
                          : <MailOutlineIcon />
                      }
                      disabled={notifyInFlight.has(job.jobId)}
                      onClick={() => handleNotifyCustomer(job)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: 'background.paper',
                        color: 'error.main',
                        '&:hover': { bgcolor: 'grey.100' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.7)', color: 'error.main' },
                      }}
                    >
                      {notifyInFlight.has(job.jobId) ? 'Sending…' : 'Notify customer'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {job.failureNotifiedAt && (
                job.failureResolvedAt ? (
                  <Tooltip title={`Resolution sent at ${new Date(job.failureResolvedAt).toLocaleString()}`} arrow>
                    <span>
                      <Button
                        data-testid={`mark-resolved-${job.jobId}`}
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        disabled
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          boxShadow: 'none',
                          bgcolor: 'rgba(255,255,255,0.85)',
                          color: 'success.dark',
                          '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.85)', color: 'success.dark' },
                        }}
                      >
                        Resolved {new Date(job.failureResolvedAt).toLocaleDateString()}
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={`Email ${job.userEmail} — "good news, your failed run is resolved"`} arrow>
                    <span>
                      <Button
                        data-testid={`mark-resolved-${job.jobId}`}
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={
                          resolveInFlight.has(job.jobId)
                            ? <CircularProgress size={14} thickness={5} />
                            : <CheckCircleOutlineIcon />
                        }
                        disabled={resolveInFlight.has(job.jobId)}
                        onClick={() => handleMarkResolved(job)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: 'background.paper',
                          color: 'success.main',
                          '&:hover': { bgcolor: 'grey.100' },
                          '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.7)', color: 'success.main' },
                        }}
                      >
                        {resolveInFlight.has(job.jobId) ? 'Sending…' : 'Mark resolved'}
                      </Button>
                    </span>
                  </Tooltip>
                )
              )}
            </Box>
          )}
        </Box>
      )}
      {typeof job.rating === 'number' && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: job.rating <= 2 ? 'warning.light' : 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            User Rating
          </Typography>
          <Typography variant="body2">
            <strong>{'★'.repeat(job.rating)}{'☆'.repeat(5 - job.rating)}</strong>{' '}
            ({job.rating}/5)
            {job.ratedAt && (
              <span style={{ color: '#6b7280', marginLeft: 8 }}>
                rated on {new Date(job.ratedAt).toLocaleString()}
              </span>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );

  /** One table row + its expandable detail. Shared by standalone runs, chat parents, and chat turns. */
  const renderJobRow = (job: RecentJob, collapse: React.ReactNode, turnCount?: number) => (
    <React.Fragment key={job.jobId}>
      <TableRow hover sx={{ '& > *': { borderBottom: expandedRows.has(job.jobId) ? 'none' : undefined } }}>
        <TableCell>
          <IconButton
            size="small"
            data-testid={`expand-row-${job.jobId}`}
            onClick={() => toggleRowExpansion(job.jobId)}
            sx={{ padding: '4px' }}
          >
            {expandedRows.has(job.jobId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        {renderRunCells(job, turnCount)}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={expandedRows.has(job.jobId)} timeout="auto" unmountOnExit>
            {collapse}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );

  /** A Doc Assist chat's expanded body: each turn (upload + follow-ups) as its own row. */
  const renderConversationTurns = (group: RecentRunGroup) => (
    <Box sx={{ margin: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {group.turnCount} {group.turnCount === 1 ? 'message' : 'messages'} in this chat
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small" data-testid={`conversation-turns-${group.groupId}`}>
          <TableHead sx={{ backgroundColor: 'action.hover' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Processing Time</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Input Tokens</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Output Tokens</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Thinking (est)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Cost</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.turns.map((turn) => renderJobRow(turn, renderRunDetail(turn)))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Overview for period:
          </Typography>
          <Chip
            label={stats?.period || '-'}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Platform Saturation Row — top-of-dashboard placement because hitting
          the Lambda concurrency cap is the single most user-impacting
          failure mode. Internal build only: its data source is internal-only
          server-side, so on the broker build it could only ever render the
          permanently-dead "—" state (see the fetch effect above). */}
      {!IS_BROKER_ADMIN && (
        <PlatformSaturationRow health={awsHealth} error={awsHealthError} />
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* AI Provider Health is the first tile so a "credit balance too low"
            or 401-key-revoked failure is the very first thing the eye lands
            on when the dashboard opens. Receipt: 2026-05-04 — Mon morning
            credit-exhaustion incident silently killed traffic with no
            in-app signal. */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ProviderHealthCard health={stats?.providerHealth} />
        </Grid>
        {/* GCV sits immediately beside Anthropic for the same reason: it is the
            other upstream that can silently kill production. Every scanned-PDF
            and DOCX-image OCR path runs through it, so a lapsed Google billing
            account fails every such job while the app looks fine. */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ProviderHealthCard
            health={stats?.gcvHealth}
            title="OCR Provider (Google Vision)"
            billingUrl={GCV_BILLING_URL}
            billingLinkLabel="Open Google Cloud billing"
            testIdPrefix="gcv-health"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Users"
            value={stats?.totalUsers ?? null}
            icon={<PeopleIcon />}
            color="#3182ce"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Organizations"
            value={stats?.totalOrganizations ?? null}
            icon={<BusinessIcon />}
            color="#805ad5"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Jobs Today"
            value={stats?.jobsToday ?? null}
            icon={<JobIcon />}
            color="#38a169"
            subtitle={
              stats?.ancillaryJobsToday
                ? `+${stats.ancillaryJobsToday} chats & actions (not counted)`
                : undefined
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Cost Today"
            value={stats?.totalCostToday !== undefined ? `$${stats.totalCostToday.toFixed(2)}` : null}
            icon={<JobIcon />}
            color="#e53e3e"
            subtitle={
              stats?.cost30dAvg != null
                ? `30d avg $${stats.cost30dAvg.toFixed(2)}/day`
                : undefined
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Currently Processing"
            value={stats?.activeProcessing ?? null}
            icon={<ProcessingIcon />}
            color="#dd6b20"
          />
        </Grid>
      </Grid>

      {/* Job Status Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompletedIcon sx={{ color: 'success.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.COMPLETED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Completed Today
                  </Typography>
                  {/* Chat turns, Report Actions, ingestion steps and admin
                      operations are registry rows but not runs, so the backend
                      leaves them out of this count. Surface the number here so
                      the tile reconciles against the Recent Activity table
                      rather than looking mysteriously low. */}
                  {!!stats?.ancillaryJobsToday && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      display="block"
                      data-testid="ancillary-jobs-today"
                    >
                      excl. {stats.ancillaryJobsToday} chats &amp; actions
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FailedIcon sx={{ color: 'error.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.FAILED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Failed Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ProcessingIcon sx={{ color: 'warning.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.PROCESSING || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Processing
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <JobIcon sx={{ color: 'info.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.QUEUED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Queued
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Daily Cost Trend - Last 30 Days */}
      {stats?.dailyCosts && stats.dailyCosts.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Cost Trend (Last 30 Days)
              </Typography>
              <Chip 
                label={`Total: $${stats.dailyCosts.reduce((sum, day) => sum + day.cost, 0).toFixed(2)}`}
                size="medium"
                sx={{ 
                  backgroundColor: '#e53e3e15',
                  color: '#e53e3e',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              />
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.dailyCosts}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `$${value.toFixed(2)}`}
                  />
                  <RechartsTooltip 
                    formatter={(value: number) => [`$${value.toFixed(2)}`, 'Cost']}
                    labelFormatter={(date: string) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cost" 
                    stroke="#e53e3e" 
                    strokeWidth={2}
                    dot={{ fill: '#e53e3e', r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Daily Successful Runs - Last 30 Days */}
      {stats?.dailyCompletedJobs && stats.dailyCompletedJobs.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Successful Runs (Last 30 Days)
              </Typography>
              <Chip
                label={`Total: ${stats.dailyCompletedJobs.reduce((sum, day) => sum + day.count, 0).toLocaleString()}`}
                size="medium"
                sx={{
                  backgroundColor: '#38a16915',
                  color: '#38a169',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              />
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.dailyCompletedJobs}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    formatter={(value: number) => [value.toLocaleString(), 'Successful Runs']}
                    labelFormatter={(date: string) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  />
                  <Bar
                    dataKey="count"
                    fill="#38a169"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Average Processing Time - Last 30 Days */}
      {stats?.dailyAverageProcessingTime && stats.dailyAverageProcessingTime.length > 0 && (
        <Card sx={{ mb: 4 }} data-testid="avg-processing-time-chart">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Average Processing Time (Last 30 Days)
              </Typography>
              {(() => {
                const totalSamples = stats.dailyAverageProcessingTime.reduce((sum, d) => sum + d.sampleSize, 0);
                const totalMs = stats.dailyAverageProcessingTime.reduce((sum, d) => sum + d.averageMs * d.sampleSize, 0);
                const overallAvg = totalSamples > 0 ? Math.round(totalMs / totalSamples) : 0;
                return (
                  <Chip
                    label={`30-day mean: ${formatProcessingTimeShort(overallAvg)}`}
                    size="medium"
                    sx={{
                      backgroundColor: '#3182ce15',
                      color: '#3182ce',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}
                  />
                );
              })()}
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.dailyAverageProcessingTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value: number) => formatProcessingTimeShort(value)}
                  />
                  <RechartsTooltip
                    formatter={(value: number, _name, item: any) => {
                      const sampleSize = item?.payload?.sampleSize ?? 0;
                      return [`${formatProcessingTimeShort(value)} (n=${sampleSize})`, 'Avg processing time'];
                    }}
                    labelFormatter={(date: string) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  />
                  <Line
                    type="monotone"
                    dataKey="averageMs"
                    stroke="#3182ce"
                    strokeWidth={2}
                    dot={{ fill: '#3182ce', r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Top 10 Slowest Runs (Last 30 Days)
              </Typography>
              <Box sx={{ height: 360 }} data-testid="slowest-runs-chart">
                {slowestRunsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={slowestRunsData} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value: number) => formatProcessingTimeShort(value)}
                      />
                      <YAxis
                        dataKey="label"
                        type="category"
                        width={210}
                        tick={{ fontSize: 11 }}
                        interval={0}
                      />
                      <RechartsTooltip content={<SlowestRunsTooltipContent />} />
                      <Bar dataKey="processingTimeMs" fill="#dd6b20" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No completed runs in the last 30 days</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Job Status Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {statusData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No status data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Recent Jobs Table */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Recent Activity (Latest Runs)
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField
                size="small"
                placeholder="Search by Job ID…"
                value={searchJobId}
                onChange={(e) => setSearchJobId(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ opacity: 0.6 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchJobId ? (
                    <InputAdornment position="end">
                      {searchLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                      <IconButton
                        size="small"
                        onClick={() => setSearchJobId('')}
                        aria-label="Clear search"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ flex: '1 1 320px', maxWidth: 520 }}
              />
              <TextField
                select
                size="small"
                label="Status"
                value={statusFilter}
                disabled={!!searchJobId.trim()}
                onChange={(e) => {
                  setStatusFilter(e.target.value as typeof statusFilter);
                  setPage(0);
                }}
                inputProps={{ 'aria-label': 'Filter by status' }}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="PROCESSING">Processing</MenuItem>
                <MenuItem value="QUEUED">Queued</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
              </TextField>
            </Box>
            {searchJobId.trim() && searchError && !searchLoading && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {searchError}
              </Alert>
            )}
          </Box>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', position: 'relative' }}>
            {tableLoading && (
              <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255,255,255,0.6)',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <Table size="small">
              <TableHead sx={{ backgroundColor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Processing Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Input Tokens</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Output Tokens</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                    <Tooltip title="Estimated thinking tokens (chars ÷ 3.5). Anthropic bundles thinking into output_tokens — chars are the actual signal." arrow>
                      <span>Thinking (est)</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Cost</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                    <Tooltip title={CREDITS_TOOLTIP} arrow>
                      <span>Credits</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const isSearching = searchJobId.trim().length > 0;
                  const displayedGroups: RecentRunGroup[] = isSearching
                    ? (searchResult ? [singletonGroup(searchResult)] : [])
                    : toRunGroups(stats).groups;
                  return displayedGroups.length > 0 ? (
                  displayedGroups.map((group) =>
                    group.isConversation
                      ? renderJobRow(group.parent, renderConversationTurns(group), group.turnCount)
                      : renderJobRow(group.parent, renderRunDetail(group.parent))
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {isSearching
                          ? (searchLoading ? 'Searching…' : 'No job matches that ID')
                          : 'No recent jobs found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
          {!searchJobId.trim() && (
            <TablePagination
              component="div"
              count={toRunGroups(stats).total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[ADMIN_RUNS_PER_PAGE]}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          )}
        </CardContent>
      </Card>

      {/* Generated timestamp */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'right' }}>
        Last updated: {stats?.generatedAt ? new Date(stats.generatedAt).toLocaleString() : '-'}
      </Typography>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DashboardPage;

